import styled from 'styled-components'

import { useDisplayClearBtn } from 'hooks/useDisplayClearBtn'
import _ from 'lodash'
import { ListRequestParams } from 'modules/domain/types'
import { FC, ReactNode, SetStateAction } from 'react'
import { AClearFiltersButton } from 'views/components/Analytics'
import { AssigneesFilter } from 'views/components/AssigneesFilter/AssigneesFilter'
import { FiltersWrapper } from 'views/components/CommonTableComponents/CommonTableComponents'
import { CompaniesFilter } from 'views/components/CompaniesFilter/CompaniesFilter'
import { ADateRangePicker } from 'views/components/TableFilters/DateRangePicker/ADateRangePicker'
import { CommonRegionFilter } from 'views/components/TableFilters/RegionsFilters/CommonRegionFilter'
import { NewTeamFilter } from 'views/components/TeamFilter/NewTeamFilter'
import { CallsShipedDealFilter } from './components/CallsShipedDealFilter'
import { CallSignFilter } from './components/CallsSignFilter'
import { DashboardSortFilter } from './components/DashboardSortFilter'
import { MatchesMarginMinFilter } from './components/MatchesMarginMinFilter'
import { MatchesSignFilter } from './components/MatchesSignFilter'
import { ResponsibleDCFilter } from './components/ResponsibleDCFilter'
import { StatusFilter } from './components/StatusFilter'
import { UserStatusFilter } from './components/UserStatusFilter'
import { excludedFilters } from './hooks/useDashboardTabsConfig'
import { DashboardTab } from './types'

const Wrapper = styled.div`
  display: flex;
  background-color: inherit;
`

const StyledFiltersWrapper = styled(FiltersWrapper)`
  margin-bottom: 20px;
`

type DashboardFiltersProps = {
  activeTab: DashboardTab
  listRequestParams: ListRequestParams
  updateCommonFilterState: (filterObj: Record<string, any>) => void
  updateFilterState: (filterObj: Record<string, any>) => void
  resetFilters: (value?: Record<string, any>) => void
  tabSetter: React.Dispatch<SetStateAction<ListRequestParams>>
}

export const DashboardFilters: FC<DashboardFiltersProps> = ({
  updateFilterState,
  updateCommonFilterState,
  resetFilters,
  listRequestParams,
  tabSetter,
  activeTab,
}) => {
  const areFiltersSet = useDisplayClearBtn({ listRequestParams })
  const areFiltersSetActiveTab = useDisplayClearBtn({ listRequestParams })
  const displayClearBtn = areFiltersSet || listRequestParams.sort || areFiltersSetActiveTab

  const filters: Record<string, ReactNode> = {
    last_bid_date_after: (
      <ADateRangePicker
        id="dashboardDateFilter"
        value={[listRequestParams.filter.last_bid_date_after, listRequestParams.filter.last_bid_date_before]}
        onChange={(value) =>
          updateFilterState({ last_bid_date_after: value.startDate, last_bid_date_before: value.endDate })
        }
      />
    ),
    team: <NewTeamFilter value={listRequestParams.filter.team} updateFilterState={updateCommonFilterState} />,
    country: <CommonRegionFilter listRequestParams={listRequestParams} updateFilterState={updateCommonFilterState} />,
    user_responsibles: (
      <ResponsibleDCFilter value={listRequestParams.filter.user_responsibles} updateFilterState={updateFilterState} />
    ),
    assignee: <AssigneesFilter value={listRequestParams.filter.assignee} updateFilterState={updateCommonFilterState} />,
    company: <CompaniesFilter value={listRequestParams.filter.company} updateFilterState={updateCommonFilterState} />,
    has_shipped_deals: (
      <MatchesSignFilter value={listRequestParams.filter.has_shipped_deals} updateFilterState={updateFilterState} />
    ),
    sort: <DashboardSortFilter listRequestParams={listRequestParams} tabSetter={tabSetter} />,
    match_margin_min: (
      <MatchesMarginMinFilter value={listRequestParams.filter.match_margin_min} updateFilterState={updateFilterState} />
    ),
    is_user_significant: (
      <UserStatusFilter
        filterKey="is_user_significant"
        listRequestParams={listRequestParams}
        updateFilterState={updateFilterState}
      />
    ),
    is_user_significant_flag: (
      <CallSignFilter value={listRequestParams.filter.is_user_significant_flag} updateFilterState={updateFilterState} />
    ),
    company_size: <StatusFilter value={listRequestParams.filter.company_size} updateFilterState={updateFilterState} />,
    has_shipped_deals_flag: (
      <CallsShipedDealFilter
        value={listRequestParams.filter.has_shipped_deals_flag}
        updateFilterState={updateFilterState}
      />
    ),
  }

  const excludedCurrTabFilters = excludedFilters[activeTab]
  const allFilterKeys = Object.keys(filters)
  const renderFilterKeys = _.difference(allFilterKeys, excludedCurrTabFilters)

  return (
    <Wrapper>
      <StyledFiltersWrapper>
        {renderFilterKeys.map((key) => filters[key])}
        {displayClearBtn && <AClearFiltersButton onClick={() => resetFilters()} />}
      </StyledFiltersWrapper>
    </Wrapper>
  )
}
